import { LIST_GC_TIME_MS, LIST_STALE_TIME_MS, useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { PaginatedResponse, User } from '@klareospace/pharos-lib-types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { AvailableDriversParams } from '../api';
import { createVehicleApi, vehicleKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseAvailableDriversOptions extends AvailableDriversParams {
  enabled?: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Lists drivers available to be set as a vehicle's default driver. "Available" is derived from
 * vehicle.defaultDriverId (the source of truth) on the backend — a driver already assigned to
 * another vehicle is excluded, while the edited vehicle's own driver remains selectable.
 */
export function useAvailableDrivers(options: UseAvailableDriversOptions): UseQueryResult<PaginatedResponse<User>> {
  const client = useApiClient('admin-auth');
  const api = createVehicleApi(client);

  const { enabled, ...params } = options;

  return useQuery({
    enabled,
    gcTime: LIST_GC_TIME_MS,
    queryFn: (): Promise<PaginatedResponse<User>> => api.getAvailableDrivers(params),
    queryKey: vehicleKeys.availableDrivers(params),
    staleTime: LIST_STALE_TIME_MS,
  });
}
